import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { FormControl, MenuItem, Select, Stack, TextField } from '@mui/material';
import {
  FlowBuilder2ActionFieldNamesEnum,
  useFlowBuilder2ActionValidators,
  useFlowBuilder2Context,
  useEditorStore,
  editorUtils,
  FieldLabel,
  FormHeader,
  FormTitle,
} from '@diana/core';
import { useFindActiveTask } from '../../../Support/useFindActiveTask';
import { ConfigureActionActivities } from './ConfigureActionActivities';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { ConfigureDrawerContent } from '../ConfigureDrawer.styles';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';

export const ConfigureAction = (props: { actionIndex: number }) => {
  const { actionIndex } = props;
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const findActiveTask = useFindActiveTask();

  const setTaskActionIndex = useFlowBuilder2PageStore((s) => s.setTaskActionIndex);

  const getFlow = useEditorStore((s) => s.getFlow);
  const actionSetId = useEditorStore((s) => findActiveTask(s.flow)?.actionSetId ?? '');

  const action = useEditorStore((s) => {
    return findActiveTask(s.flow)?.actions[actionIndex] ?? null;
  }, isEqual);

  const [isDisabled, setDisabled] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [completionStatus, setCompletionStatus] = useState<string>('');

  const disabled = !action || isDisabled;

  const validators = useFlowBuilder2ActionValidators();

  const { updateAndSaveActionSetById } = useFlowBuilder2Context();

  const isStopAction = useMemo(() => {
    return !action || editorUtils.isStopAction(action);
  }, [action]);

  const displayNameValidationResult = useMemo(() => {
    const flow = getFlow();
    const actionSet = findActiveTask(flow);
    const validate = validators[FlowBuilder2ActionFieldNamesEnum.DisplayName];
    if (flow && actionSet && action) return validate({ flow, actionSet, action });
    return null;
  }, [getFlow, findActiveTask, validators, action]);

  const completionStatusValidationResult = useMemo(() => {
    const flow = getFlow();
    const actionSet = findActiveTask(flow);
    const validate = validators[FlowBuilder2ActionFieldNamesEnum.CompletionStatus];
    if (flow && actionSet && action) return validate({ flow, actionSet, action });
    return null;
  }, [getFlow, findActiveTask, validators, action]);

  const stopStatusMap: Partial<Record<string, string>> = {
    Cancelled: t('labels.cancelled'),
    Declined: t('labels.declined'),
  };

  const updateAndSaveActionSetByIdDebounce = useDebouncedCallback(updateAndSaveActionSetById, 1500);

  useLayoutEffect(() => {
    setDisplayName(action?.displayName ?? '');
  }, [action?.displayName]);

  useLayoutEffect(() => {
    if (isStopAction) setCompletionStatus(action?.executeStatus ?? '');
    else setCompletionStatus(action?.completedName ?? '');
  }, [isStopAction, action?.executeStatus, action?.completedName]);

  useEffect(
    () => updateAndSaveActionSetByIdDebounce.flush,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    setDisabled(!action);
  }, [action]);

  useEffect(() => {
    return () => {
      setTaskActionIndex(-1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConfigureDrawerContent gap={2}>
      <Stack gap={2}>
        <Stack gap={2} sx={{ opacity: disabled ? 0.5 : 1 }}>
          <TextField
            label={<FieldLabel label={t('labels.buttonAction')} required />}
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              updateAndSaveActionSetByIdDebounce(actionSetId, (draft) => {
                draft.actions[actionIndex].displayName = e.target.value;
                return draft;
              });
            }}
            variant="standard"
            error={displayNameValidationResult?.type === 'error'}
            helperText={displayNameValidationResult?.message}
            fullWidth
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            placeholder={t('prompts.enterActionName')}
          />

          <Stack>
            {isStopAction ? (
              <FormControl variant="standard" fullWidth>
                <FormHeader>
                  <FormTitle title={t('labels.statusName')} />
                </FormHeader>
                <Select
                  disabled={disabled}
                  value={completionStatus}
                  onChange={(e) => {
                    setCompletionStatus(e.target.value);
                    updateAndSaveActionSetByIdDebounce(actionSetId, (draft) => {
                      const shouldSyncActionSetDisplayName =
                        draft.actionSetDisplayName === draft.actions[actionIndex].completedName;
                      draft.actions[actionIndex].completedName = e.target.value;
                      draft.actions[actionIndex].executeStatus = e.target.value as any;
                      if (shouldSyncActionSetDisplayName) {
                        draft.displayName = e.target.value;
                        draft.actionSetDisplayName = e.target.value;
                      }
                      return draft;
                    });
                  }}
                >
                  {!!completionStatus && !stopStatusMap[completionStatus] && (
                    <MenuItem value={completionStatus} disabled>
                      {completionStatus}
                    </MenuItem>
                  )}
                  {Array.from(Object.entries(stopStatusMap)).map(([value, displayName]) => (
                    <MenuItem value={value}>{displayName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label={<FieldLabel label={t('labels.statusName')} />}
                value={completionStatus}
                onChange={(e) => {
                  setCompletionStatus(e.target.value);
                  updateAndSaveActionSetByIdDebounce(actionSetId, (draft) => {
                    const shouldSyncActionSetDisplayName =
                      draft.actionSetDisplayName === draft.actions[actionIndex].completedName;
                    draft.actions[actionIndex].completedName = e.target.value;
                    if (shouldSyncActionSetDisplayName) {
                      draft.displayName = e.target.value;
                      draft.actionSetDisplayName = e.target.value;
                    }
                    return draft;
                  });
                }}
                variant="standard"
                error={completionStatusValidationResult?.type === 'error'}
                helperText={completionStatusValidationResult?.message}
                fullWidth
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
      <ConfigureActionActivities disabled={disabled} actionIndex={actionIndex} />
    </ConfigureDrawerContent>
  );
};
